import React, {PureComponent} from 'react'
import PropTypes from 'prop-types'
import {getComponentDisplayName} from '@modifi/utilities'

import detectPassiveEventsSupport from './detectPassiveEventsSupport'

const getCurrentScrollPosition = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.scrollTop || document.body.scrollTop
  }
  return 0
}

export const ScrollContext = React.createContext({
  scrollPosition: 0,
})

class ScrollProvider extends PureComponent {
  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.element,
      PropTypes.object,
      PropTypes.func,
    ]).isRequired,
  }

  usePassive = false

  constructor(props) {
    super(props)

    this.usePassive = detectPassiveEventsSupport()
    this.state = {
      scrollPosition: getCurrentScrollPosition(),
    }

    this.handleScroll = this.handleScroll.bind(this)
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      if (this.usePassive) {
        window.addEventListener('scroll', this.handleScroll, {passive: true})
      } else {
        window.addEventListener('scroll', this.handleScroll)
      }
    }
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.handleScroll)
    }
  }

  handleScroll() {
    this.setState({
      scrollPosition: getCurrentScrollPosition(),
    })
  }

  render() {
    const {scrollPosition} = this.state
    const {children} = this.props

    const context = {
      scrollPosition,
    }

    return <ScrollContext.Provider value={context}>{children}</ScrollContext.Provider>
  }
}

export const withScrollProvider = WrappedComponent => {
  const ScopedComponent = props => (
    <ScrollProvider>
      <WrappedComponent {...props} />
    </ScrollProvider>
  )

  ScopedComponent.WrappedComponent = WrappedComponent
  ScopedComponent.displayName = `withScrollProvider(${getComponentDisplayName(WrappedComponent)})`

  return ScopedComponent
}

export const withScrollHelper = WrappedComponent => {
  const ScopedComponent = React.forwardRef((props, ref) => (
    <ScrollContext.Consumer>
      {contextParams => <WrappedComponent innerRef={ref} {...contextParams} {...props} />}
    </ScrollContext.Consumer>
  ))

  ScopedComponent.WrappedComponent = WrappedComponent
  ScopedComponent.displayName = `withScrollHelper(${getComponentDisplayName(WrappedComponent)})`

  return ScopedComponent
}
